import { getShops } from '@/services/stores';
import { addBreadcrumb, createWarning } from '@/utils/monitoring.utils';
import { PAYMENT_PLAN_MERCHANTS } from '@/utils/paymentPlanMerchants.utils';
import { defineStore } from 'pinia';
import { computed, ref, watch } from 'vue';
import type { ModelMerchantShopDirectoryShopV2 } from '@layup/api-sdk';

const FIFTEEN_MINUTES = 15 * 60 * 1000;

const initializeTypes = (product: 'layby' | 'plans' | 'boost') => {
  const arr: { name: string; selected: boolean }[] = [{ name: 'Online', selected: false }];
  if (product === 'layby' || product === 'plans') arr.push({ name: 'In-store', selected: false });
  return arr;
};

const initializeDurations = () => {
  const arr: { name: string; value: number | string; selected: boolean }[] = [];
  for (let i = 3; i <= 12; i++) arr.push({ name: `${i} Months`, value: i, selected: false });
  arr.push({ name: '> 12 Months', value: '12+', selected: false });
  return arr;
};

const initializeRewardPercentages = () => [
  { name: '0-10%', selected: false },
  { name: '10-20%', selected: false },
  { name: '20-30%', selected: false },
  { name: '30%+', selected: false },
];

const initializeMinimumAmounts = () => [
  { name: '< R1000', selected: false },
  { name: 'R1000', selected: false },
  { name: 'R2000', selected: false },
  { name: 'R3000', selected: false },
  { name: 'R4000', selected: false },
  { name: 'R5000+', selected: false },
];

export const useShopsStore = defineStore('shops', () => {
  const currentProduct = ref<'layby' | 'plans' | 'boost'>('layby');
  const allShops = ref<ModelMerchantShopDirectoryShopV2[]>([]);
  const categories = ref<string[]>([]);
  const loading = ref(false);
  const error = ref<string | null>(null);
  const lastFetchedAt = ref<number | null>(null);

  const filterCategories = ref<{ name: string; selected: boolean }[]>([]);
  const filterTypes = ref(initializeTypes('layby'));
  const filterDurations = ref(initializeDurations());
  const filterRewardPercentages = ref(initializeRewardPercentages());
  const filterMinimumAmounts = ref(initializeMinimumAmounts());
  const sortBy = ref<'a-z' | 'z-a' | 'newest' | 'default'>('default');

  watch(categories, (newCats) => {
    if (newCats.length > 0 && filterCategories.value.length === 0) {
      filterCategories.value = newCats.map((cat) => ({ name: cat, selected: false }));
    }
  });

  watch(currentProduct, () => {
    filterTypes.value = initializeTypes(currentProduct.value);
    filterDurations.value = initializeDurations();
    filterRewardPercentages.value = initializeRewardPercentages();
    filterMinimumAmounts.value = initializeMinimumAmounts();
  });

  const selectedCategories = computed(() => filterCategories.value.filter((c) => c.selected).map((c) => c.name));
  const selectedTypes = computed(() => filterTypes.value.filter((t) => t.selected).map((t) => t.name));
  const selectedDurations = computed(() => filterDurations.value.filter((d) => d.selected).map((d) => d.value));
  const selectedRewardPercentages = computed(() =>
    filterRewardPercentages.value.filter((p) => p.selected).map((p) => p.name),
  );
  const selectedMinimumAmounts = computed(() =>
    filterMinimumAmounts.value.filter((a) => a.selected).map((a) => a.name),
  );

  const shops = computed(() => {
    let filtered = [...allShops.value];

    if (currentProduct.value === 'boost') {
      filtered = filtered.filter((s) => s.boostRewards?.enabled === true);
    } else if (currentProduct.value === 'plans') {
      filtered = filtered.filter(
        (s) => !s.boostRewards?.enabled && PAYMENT_PLAN_MERCHANTS.includes(s.name?.toUpperCase() ?? ''),
      );
    } else {
      filtered = filtered.filter(
        (s) => !s.boostRewards?.enabled && !PAYMENT_PLAN_MERCHANTS.includes(s.name?.toUpperCase() ?? ''),
      );
    }

    if (selectedCategories.value.length > 0) {
      filtered = filtered.filter((s) => s.categories?.some((c) => selectedCategories.value.includes(c)));
    }

    if (selectedTypes.value.length > 0) {
      filtered = filtered.filter((s) => {
        if (!s.availability) return false;
        const avail = s.availability.toLowerCase().replace(/-/g, ' ');
        return (
          (selectedTypes.value.includes('Online') && avail.includes('online')) ||
          (selectedTypes.value.includes('In-store') && avail.includes('in store'))
        );
      });
    }

    if ((currentProduct.value === 'layby' || currentProduct.value === 'plans') && selectedDurations.value.length > 0) {
      filtered = filtered.filter((s) => {
        const pp = s.paymentPlan;
        if (!pp?.enabled || pp.maxMonths == null || pp.minMonths == null) return false;
        return selectedDurations.value.some((d) => (d === '12+' ? pp.maxMonths! > 12 : pp.maxMonths! >= (d as number)));
      });
    }

    if (currentProduct.value === 'boost' && selectedRewardPercentages.value.length > 0) {
      filtered = filtered.filter((s) => {
        const r = s.boostRewards?.rewardPercentage;
        if (r == null) return false;
        return selectedRewardPercentages.value.some((sel) => {
          if (sel === '0-10%') return r >= 0 && r <= 10;
          if (sel === '10-20%') return r > 10 && r <= 20;
          if (sel === '20-30%') return r > 20 && r <= 30;
          if (sel === '30%+') return r > 30;
          return false;
        });
      });
    }

    if (currentProduct.value === 'boost' && selectedMinimumAmounts.value.length > 0) {
      filtered = filtered.filter((s) => {
        const min = s.boostRewards?.minSavingsAmount;
        if (min == null) return false;
        return selectedMinimumAmounts.value.some((sel) => {
          if (sel === '< R1000') return min < 100000;
          if (sel === 'R1000') return min >= 100000;
          if (sel === 'R2000') return min >= 200000;
          if (sel === 'R3000') return min >= 300000;
          if (sel === 'R4000') return min >= 400000;
          if (sel === 'R5000+') return min >= 500000;
          return false;
        });
      });
    }

    if (sortBy.value === 'a-z') filtered.sort((a, b) => (a.name || '').localeCompare(b.name || ''));
    else if (sortBy.value === 'z-a') filtered.sort((a, b) => (b.name || '').localeCompare(a.name || ''));
    else if (sortBy.value === 'newest')
      filtered.sort((a, b) => new Date(b.createdAt || 0).getTime() - new Date(a.createdAt || 0).getTime());

    return filtered;
  });

  const featuredShops = computed(() => {
    if (currentProduct.value === 'boost') {
      return allShops.value.filter((s) => s.boostRewards?.enabled === true && s.featured === true);
    }
    if (currentProduct.value === 'plans') {
      return allShops.value.filter(
        (s) =>
          !s.boostRewards?.enabled &&
          PAYMENT_PLAN_MERCHANTS.includes(s.name?.toUpperCase() ?? '') &&
          s.featured === true,
      );
    }
    return allShops.value.filter((s) => !s.boostRewards?.enabled && s.featured === true);
  });

  const homeFeaturedShops = computed(() => allShops.value.filter((s) => s.featured === true));

  const fetchStores = async (force = false) => {
    const now = Date.now();
    if (!force && lastFetchedAt.value && now - lastFetchedAt.value < FIFTEEN_MINUTES) return;

    loading.value = true;
    error.value = null;
    try {
      const response = await getShops();
      allShops.value = response.shops ?? [];
      categories.value = response.categories ?? [];
      lastFetchedAt.value = Date.now();
    } catch (err) {
      error.value = 'Failed to load store directory.';
      addBreadcrumb('shop.fetch', 'Failed to fetch shops from API');
      createWarning(err);
    } finally {
      loading.value = false;
    }
  };

  const clearFilters = () => {
    filterCategories.value.forEach((c) => (c.selected = false));
    filterTypes.value.forEach((t) => (t.selected = false));
    filterDurations.value.forEach((d) => (d.selected = false));
    filterRewardPercentages.value.forEach((p) => (p.selected = false));
    filterMinimumAmounts.value.forEach((a) => (a.selected = false));
  };

  const setSortBy = (sort: 'a-z' | 'z-a' | 'newest' | 'default') => {
    sortBy.value = sort;
  };

  return {
    currentProduct,
    shops,
    featuredShops,
    homeFeaturedShops,
    categories,
    loading,
    error,
    lastFetchedAt,
    filterCategories,
    filterTypes,
    filterDurations,
    filterRewardPercentages,
    filterMinimumAmounts,
    sortBy,
    setSortBy,
    selectedCategories,
    selectedTypes,
    selectedDurations,
    selectedRewardPercentages,
    selectedMinimumAmounts,
    fetchStores,
    clearFilters,
  };
});
